/**
 * Draft Service - Auto-Save und Restore fuer Anamnese-Formulardaten
 *
 * Strategie:
 * - formData wird alle 30s in localStorage gespeichert (Klartext, nur lokal)
 * - formData wird alle 30s verschluesselt an den Server gesendet (als Backup)
 * - localStorage hat Vorrang (enthaelt Klartext, sofort nutzbar)
 * - Server-Draft ist verschluesselt (nur LISA-Server kann entschluesseln)
 */

import { saveDraft as apiSaveDraft, loadDraft as apiLoadDraft } from './api';
import { encryptAnamneseData } from './encryption';

const STORAGE_PREFIX = 'anamnese_draft_';

/**
 * formData in localStorage speichern
 */
export function saveToLocalStorage(token, formData, medicationPhoto, acceptedDocs, guardianData) {
  try {
    const draft = {
      formData,
      medicationPhoto: medicationPhoto || null,
      acceptedDocs: acceptedDocs || {},
      guardianData: guardianData || null,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(`${STORAGE_PREFIX}${token}`, JSON.stringify(draft));
    return true;
  } catch (e) {
    console.warn('localStorage speichern fehlgeschlagen:', e);
    return false;
  }
}

/**
 * formData aus localStorage laden
 */
export function loadFromLocalStorage(token) {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${token}`);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    // Draft aelter als 7 Tage verwerfen
    if (draft.savedAt) {
      const age = Date.now() - new Date(draft.savedAt).getTime();
      if (age > 7 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(`${STORAGE_PREFIX}${token}`);
        return null;
      }
    }
    return draft;
  } catch (e) {
    console.warn('localStorage laden fehlgeschlagen:', e);
    return null;
  }
}

/**
 * Draft aus localStorage loeschen
 */
export function clearLocalStorage(token) {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${token}`);
  } catch (e) {
    // Ignorieren
  }
}

/**
 * Draft verschluesselt an Server senden (fire-and-forget)
 */
export async function saveToServer(token, formData, publicKey, patientId, keyVersion, medicationPhoto, acceptedDocs, guardianData) {
  try {
    const dataToEncrypt = {
      formData,
      medicationPhoto: medicationPhoto || null,
      acceptedDocs: acceptedDocs || {},
      guardianData: guardianData || null,
      savedAt: new Date().toISOString(),
      isDraft: true,
    };

    const encrypted = await encryptAnamneseData(
      dataToEncrypt, publicKey, patientId, token, keyVersion
    );

    await apiSaveDraft(token, encrypted);
    return true;
  } catch (e) {
    console.warn('Server-Draft speichern fehlgeschlagen:', e);
    return false;
  }
}

/**
 * Pruefen ob ein Server-Draft existiert
 */
export async function hasServerDraft(token) {
  try {
    const draft = await apiLoadDraft(token);
    return draft !== null;
  } catch (e) {
    return false;
  }
}

/**
 * Auto-Save Setup: Speichert alle intervalMs Millisekunden
 * @returns {Function} cleanup
 */
export function setupAutoSave(token, getState, publicKey, patientId, keyVersion, intervalMs = 30000) {
  let lastHash = '';

  const save = async () => {
    const { formData, medicationPhoto, acceptedDocs, guardianData } = getState();

    const currentHash = JSON.stringify(formData);
    if (currentHash === lastHash) return;
    lastHash = currentHash;

    // localStorage (schnell, synchron)
    saveToLocalStorage(token, formData, medicationPhoto, acceptedDocs, guardianData);

    // Server (async, fire-and-forget)
    saveToServer(token, formData, publicKey, patientId, keyVersion, medicationPhoto, acceptedDocs, guardianData);
  };

  const intervalId = setInterval(save, intervalMs);
  return () => clearInterval(intervalId);
}
